import { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';

// ── Types ────────────────────────────────────────────────────────────────────

export interface ScheduleCrewMember {
  userId?: string;
  displayName: string;
  isCrewLead: boolean;
  isDriver: boolean;
  isContractor: boolean;
}

export interface ScheduleTruck {
  truckId: string;
  truckName: string;
}

export interface ScheduleEvent {
  id: string;
  type: 'move' | 'estimate';
  customerCompanyName: string;
  quoteNumber?: string;
  date: string;
  timing?: string;
  quoteType?: 'commercial' | 'residential';
  lifecycleStage?: string;
  // Move-specific
  jobStatus?: string;
  jobStage?: string;
  assignedCrew?: ScheduleCrewMember[];
  assignedTrucks?: ScheduleTruck[];
  crewSize?: number;
  truckQuantity?: number;
  driverQuantity?: number;
  originAddress?: string;
  destinationAddress?: string;
  // Estimate-specific
  quoteLeadDisplayName?: string;
  quoteLeadUserId?: string;
  quoteStatus?: string;
}

export interface ScheduleResources {
  busyCrewUserIds: string[];
  busyCrewNames: string[];
  busyTruckIds: string[];
  busyTruckNames: string[];
  totalOrgMembers: number;
  totalOrgTrucks: number;
  totalMovers: number;
  totalEstimators: number;
}

interface ScheduleResponse {
  success: boolean;
  data: {
    events: ScheduleEvent[];
    dayCounts: Record<string, number>;
    resources: ScheduleResources;
  };
}

export interface ScheduleFilters {
  userId?: string;
  truckId?: string;
  stage?: 'quote' | 'job';
  type?: 'commercial' | 'residential';
  status?: string;
}

// ── Hook ─────────────────────────────────────────────────────────────────────

export function useSchedule(
  orgId: string | null,
  date: string,
  range: 'day' | 'week' = 'week',
  filters: ScheduleFilters = {},
) {
  const [events, setEvents] = useState<ScheduleEvent[]>([]);
  const [dayCounts, setDayCounts] = useState<Record<string, number>>({});
  const [resources, setResources] = useState<ScheduleResources>({
    busyCrewUserIds: [],
    busyCrewNames: [],
    busyTruckIds: [],
    busyTruckNames: [],
    totalOrgMembers: 0,
    totalOrgTrucks: 0,
    totalMovers: 0,
    totalEstimators: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { userId, truckId, stage, type, status } = filters;

  const fetchSchedule = useCallback(async () => {
    if (!orgId || !date) {
      setEvents([]);
      setDayCounts({});
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const qs = new URLSearchParams();
      qs.set('date', date);
      qs.set('range', range);
      if (userId) qs.set('userId', userId);
      if (truckId) qs.set('truckId', truckId);
      if (stage) qs.set('stage', stage);
      if (type) qs.set('type', type);
      if (status) qs.set('status', status);

      const res = await api.get<ScheduleResponse>(`/schedule/${orgId}?${qs.toString()}`);
      setEvents(res.data.events);
      setDayCounts(res.data.dayCounts);
      setResources(res.data.resources);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load schedule');
    } finally {
      setLoading(false);
    }
  }, [orgId, date, range, userId, truckId, stage, type, status]);

  useEffect(() => {
    fetchSchedule();
  }, [fetchSchedule]);

  return { events, dayCounts, resources, loading, error, refetch: fetchSchedule };
}
